import React, { useCallback, useEffect, useRef, useState } from "react";

// Modules
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import moment from "moment";

// App - deliberately no getOnlineNode/postNode: the portal is anonymous and
// cross-origin; the app helpers attach auth interceptors (401 → logout) and
// hal_json formats that must not run here. Seller preview is the exception:
// it sends the session cookie explicitly via withCredentials.
import { AppLogo, serverUrl, assetBase, subdomain } from "../../config";
import useRoomTracking, { getVisitorId } from "./useRoomTracking";
import { DEFAULT_DASHBOARD_TITLE } from "../salesRooms/salesRoomData";
import RoomItemViewer from "./roomItemViewer";
import RoomThread from "./roomThread";
import RoomUpdateCards from "./roomUpdateCards";

// UI components
import { Col, Container, Row } from "react-bootstrap";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  TextField,
  Typography,
} from "@mui/material";
import SlideshowIcon from "@mui/icons-material/SlideshowOutlined";
import DescriptionIcon from "@mui/icons-material/DescriptionOutlined";
import MovieIcon from "@mui/icons-material/MovieOutlined";
import LinkIcon from "@mui/icons-material/LinkOutlined";
import MicIcon from "@mui/icons-material/MicNoneRounded";
import CollectionsIcon from "@mui/icons-material/CollectionsOutlined";
import ArticleIcon from "@mui/icons-material/ArticleOutlined";
import EventIcon from "@mui/icons-material/CalendarTodayOutlined";
import OpenNewIcon from "@mui/icons-material/OpenInNewOutlined";
import LockIcon from "@mui/icons-material/LockOutlined";
import ChatIcon from "@mui/icons-material/ChatBubbleOutlineOutlined";
import AddCircleIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircleOutlineOutlined";
import TaskDoneIcon from "@mui/icons-material/CheckCircleOutlined";
import CloseIcon from "@mui/icons-material/CloseOutlined";
import EditIcon from "@mui/icons-material/EditOutlined";
import AddIcon from "@mui/icons-material/AddOutlined";
import SortIcon from "@mui/icons-material/ArrowDropDownOutlined";
import { Badge, IconButton, Tooltip } from "@mui/material";
import CompanyappLoader from "../../partials/companyappLoader";
import caLogo from "../../assets/ca-logo-pwa.png";

const typeMeta = {
  presentation: { icon: SlideshowIcon, label: "Presentation" },
  document: { icon: DescriptionIcon, label: "Document" },
  video: { icon: MovieIcon, label: "Video" },
  link: { icon: LinkIcon, label: "Link" },
  podcast: { icon: MicIcon, label: "Podcast" },
  gallery: { icon: CollectionsIcon, label: "Gallery" },
  news: { icon: ArticleIcon, label: "News" },
  event: { icon: EventIcon, label: "Event" },
};

// Types that open in a new tab via their public single screens.
const newTabRoutes = {
  presentation: (item) =>
    `/presentations/view/${item.id}${
      item.access_tokens?.[0]?.token
        ? `?hash=${item.access_tokens[0].token}`
        : ""
    }`,
  news: (item) => `/news/view/${item.id}?hash=${item.access_tokens || ""}`,
  event: (item) => `/events/view/${item.id}?hash=${item.access_tokens || ""}`,
};

// Content whose primary file + thumbnail live in the entity's published
// directory (dev-zip copies them into `{type}/{id}/{id}/assets/` and signs
// each with `?sig=`). The buyer portal is anonymous, so this same-origin,
// signed-in-content published dir - NOT the token_share `/shared/files/`
// stream, which can only serve node-local files and 404s on file-server
// media - is the reliable source. Mirrors the entity single screens
// (video/podcast/gallery/document + postSingle featured image); the same
// book.json the buyer's news/event tabs already read anonymously.
const PUBLISHED_ASSET_TYPES = [
  "document",
  "video",
  "podcast",
  "gallery",
  "news",
  "event",
];

const publishedUrl = (type, id, relPath) =>
  relPath ? `${assetBase}/${type}/${id}/${id}/${relPath}` : null;

// Extracts the signed, published-dir asset URLs from an entity's book.json,
// keyed to how each entity single screen builds them.
const resolvePublishedAssets = (item, book) => {
  const { id, type } = item;
  const resolved = {};

  if (book.field_featured_image) {
    resolved.thumbnail = publishedUrl(type, id, book.field_featured_image);
  }

  switch (type) {
    case "document":
      resolved.fileUrl = publishedUrl("document", id, book.field_document);
      break;
    case "video":
      // oembed videos play through their iframe embed - no file to resolve.
      if (!item.field_media_oembed_video) {
        resolved.fileUrl = publishedUrl("video", id, book.field_video);
      }
      break;
    case "podcast":
      resolved.fileUrl = publishedUrl("podcast", id, book.field_audio);
      break;
    case "gallery": {
      const images = [];
      (book.body?.blocks || [])
        .filter((block) => block.type === "gallery")
        .forEach((block) => {
          (block.data?.images || []).forEach((image) => {
            images.push({
              ...image,
              src: publishedUrl("gallery", id, image.url),
            });
          });
        });
      if (images.length) {
        resolved.images = images;
      }
      break;
    }
    default:
      break;
  }

  return resolved;
};

const errorCopy = {
  room_not_found: {
    title: "Room not found",
    body: "This link doesn't match an active sales room. Check the link or contact the person who shared it with you.",
  },
  room_not_available: {
    title: "Room not available",
    body: "This room isn't open right now. Contact the person who shared it with you for an updated link.",
  },
  room_expired: {
    title: "Room expired",
    body: "Access to this room has expired. Contact the person who shared it with you to request renewed access.",
  },
  network: {
    title: "Something went wrong",
    body: "We couldn't load the room. Please try again in a moment.",
  },
};

const IDENTITY_KEY = "dsr_buyer_identity";

// A blank action-plan step. `assignee` is filled in on open from whoever is
// looking: a buyer's step is theirs by default, a seller's is the team's.
const EMPTY_STEP = {
  id: null,
  title: "",
  description: "",
  due: "",
  assignee: "buyer",
  status: "pending",
};

// Step states. The stored keys are the backend's ('pending' and 'done' are
// the original two, kept so existing plans need no migration); `order` drives
// the Status column's sort, running not-started → in-progress → completed.
const STEP_STATUSES = {
  pending: { key: "pending", label: "Not started", order: 0 },
  in_progress: { key: "in_progress", label: "In progress", order: 1 },
  done: { key: "done", label: "Completed", order: 2 },
};

const PLAN_COLUMNS = [
  { key: "due", label: "Due date", sortable: true },
  { key: "status", label: "Status", sortable: true },
  { key: "action", label: "Action", sortable: false },
  { key: "comments", label: "Comments", sortable: false },
  { key: "owner", label: "Owner", sortable: true },
  { key: "edit", label: "Edit", sortable: false },
];

const loadIdentity = () => {
  try {
    const stored = JSON.parse(localStorage.getItem(IDENTITY_KEY));
    if (stored && stored.name && stored.email) {
      return stored;
    }
  } catch (e) {
    // fall through
  }
  return { name: "", email: "" };
};

export default function BuyerRoom() {
  const { token } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const isPreview = searchParams.get("preview") === "1";

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  // Which unlock gate to show: null | "password" | "email".
  const [gate, setGate] = useState(null);
  // Email gate stage: "input" (asking for the email) | "sent" (link emailed).
  const [gateStage, setGateStage] = useState("input");
  const [password, setPassword] = useState("");
  const [gateEmail, setGateEmail] = useState("");
  const [gateError, setGateError] = useState(null);
  const [room, setRoom] = useState(null);
  const [openItem, setOpenItem] = useState(null);
  // Published-dir asset URLs resolved from each referenced entity's book.json,
  // keyed by `${type}:${id}`. Falls back to the token_share URLs when absent.
  const [resolvedAssets, setResolvedAssets] = useState({});
  const [milestones, setMilestones] = useState([]);
  const [messages, setMessages] = useState([]);
  // The conversation now lives in a persistent right-hand column, so it is
  // open (loaded + polled) by default rather than revealed on demand.
  const [messagesOpen, setMessagesOpen] = useState(true);
  const [identity, setIdentity] = useState(loadIdentity);
  // The identity form stays visible until the buyer has actually posted
  // with it (or arrived with a stored identity) - gating visibility on the
  // live field values would unmount the row mid-typing.
  const [identityConfirmed, setIdentityConfirmed] = useState(() => {
    const stored = loadIdentity();
    return !!(stored.name && stored.email);
  });
  // Composer scope: null | {kind, content_type?, content_id?, label} -
  // set by the Request content button (general conversation only).
  const [composeContext, setComposeContext] = useState(null);
  // The entity item whose comments modal is open:
  // null | {content_type, content_id, title}. Per-item discussion lives in
  // this modal now, separate from the shared Messages conversation.
  const [commentModal, setCommentModal] = useState(null);
  // The inline "add a step" form in the Action Plan tab: open state, draft
  // values, the in-flight flag and the last error to show under the form.
  const [stepFormOpen, setStepFormOpen] = useState(false);
  const [stepDraft, setStepDraft] = useState(EMPTY_STEP);
  const [savingStep, setSavingStep] = useState(false);
  const [stepError, setStepError] = useState(null);
  // Which plan column the table is sorted by, and in which direction.
  const [planSort, setPlanSort] = useState({ key: "due", asc: true });
  // How much of the open step the viewer may change: "full" or "status".
  const [stepMode, setStepMode] = useState("full");
  // Which tab is open in the main column. Null falls back to the first tab
  // once the room (and therefore the tab list) is known.
  const [activeTabKey, setActiveTabKey] = useState(null);
  // Curated activity feed ({rows, pager}); null until the tab is first opened.
  const [activity, setActivity] = useState(null);

  const pollRef = useRef(null);
  const { track, startContentTimer, stopContentTimer } = useRoomTracking(
    token,
    isPreview,
  );

  const unlockKey = `dsr_unlock_${token}`;
  const getUnlock = () => {
    try {
      return sessionStorage.getItem(unlockKey) || "";
    } catch (e) {
      return "";
    }
  };

  // Tracking consent: nothing is recorded (and no content shown) until the
  // buyer explicitly agrees; remembered per room in this browser.
  const consentKey = `dsr_consent_${token}`;
  const hasStoredConsent = () => {
    try {
      return localStorage.getItem(consentKey) === "1";
    } catch (e) {
      return false;
    }
  };
  const [consented, setConsented] = useState(hasStoredConsent);

  const buyerUrl = useCallback(
    (path = "") => {
      const params = new URLSearchParams();
      if (isPreview) {
        params.set("preview", "1");
      }
      const unlock = getUnlock();
      if (unlock) {
        params.set("unlock", unlock);
      }
      const query = params.toString();
      return `${serverUrl}/api/v3/room/${token}${path}${query ? `?${query}` : ""}`;
    },
    // eslint-disable-next-line
    [token, isPreview],
  );

  const requestConfig = isPreview ? { withCredentials: true } : {};

  // Fire-and-forget identity binding so the seller's analytics show people
  // instead of anonymous visitor IDs.
  const identifyVisitor = useCallback(
    (email, name) => {
      if (!email || isPreview) {
        return;
      }
      fetch(buyerUrl("/identify"), {
        method: "POST",
        keepalive: true,
        headers: { "Content-Type": "text/plain" },
        body: JSON.stringify({
          visitor_id: getVisitorId(),
          email,
          name: name || "",
        }),
      }).catch(() => {});
    },
    // eslint-disable-next-line
    [buyerUrl, isPreview],
  );

  const loadRoom = useCallback(() => {
    setIsLoading(true);
    setError(null);
    setGate(null);

    axios
      .get(buyerUrl(), requestConfig)
      .then((response) => {
        setRoom(response.data);
        setMilestones(response.data.milestones || []);
        setIsLoading(false);
        // The open is only recorded once the buyer has agreed to tracking
        // (returning visitors with stored consent record it immediately).
        if (hasStoredConsent()) {
          track({ event_type: "room_opened" });
        }
      })
      .catch((_error) => {
        setIsLoading(false);
        const code = _error.response?.data?.error;
        if (code === "password_required") {
          setGate("password");
        } else if (code === "email_required") {
          setGate("email");
        } else {
          setError(code || "network");
        }
      });
    // eslint-disable-next-line
  }, [token, isPreview]);

  // Once the room loads, pull each file/thumbnail item's signed asset URLs from
  // its published book.json (same anonymous, same-origin source the news/event
  // tabs already use). Anything that fails to resolve silently keeps its
  // token_share fallback, so a missing/unpublished entity never breaks the room.
  useEffect(() => {
    if (!room || !subdomain) {
      return;
    }
    const seen = new Set();
    const targets = (room.sections || [])
      .flatMap((section) => section.items || [])
      .filter((item) => {
        if (!PUBLISHED_ASSET_TYPES.includes(item.type)) {
          return false;
        }
        const key = `${item.type}:${item.id}`;
        if (seen.has(key)) {
          return false;
        }
        seen.add(key);
        return true;
      });
    if (!targets.length) {
      return;
    }

    let cancelled = false;
    Promise.allSettled(
      targets.map((item) =>
        axios
          .get(`${assetBase}/${item.type}/${item.id}/${item.id}/book.json`)
          .then((response) => ({ item, book: response.data })),
      ),
    ).then((results) => {
      if (cancelled) {
        return;
      }
      const map = {};
      results.forEach((result) => {
        if (result.status !== "fulfilled" || !result.value?.book) {
          return;
        }
        const { item, book } = result.value;
        map[`${item.type}:${item.id}`] = resolvePublishedAssets(item, book);
      });
      if (Object.keys(map).length) {
        setResolvedAssets((prev) => ({ ...prev, ...map }));
      }
    });

    return () => {
      cancelled = true;
    };
  }, [room]);

  useEffect(() => {
    // A clicked magic link lands here with ?verify= - exchange it for the
    // unlock proof, then clean the URL so refresh/bookmark doesn't re-send
    // a consumed (or expired) token.
    const verify = searchParams.get("verify");
    if (verify) {
      const stripVerify = () => {
        const next = new URLSearchParams(searchParams);
        next.delete("verify");
        setSearchParams(next, { replace: true });
      };

      axios
        .post(`${serverUrl}/api/v3/room/${token}/unlock`, { verify })
        .then((response) => {
          try {
            sessionStorage.setItem(unlockKey, response.data.unlock || "");
          } catch (e) {
            // Storage unavailable - proof still applies to this load.
          }
          if (response.data.email) {
            setIdentity((prev) => ({ ...prev, email: response.data.email }));
            identifyVisitor(response.data.email, "");
          }
          stripVerify();
          loadRoom();
        })
        .catch((_error) => {
          const code = _error.response?.data?.error;
          stripVerify();
          setIsLoading(false);
          setGate("email");
          setGateStage("input");
          setGateError(
            code === "verify_expired"
              ? "That access link has expired - request a new one below."
              : "That access link isn't valid - request a new one below.",
          );
        });
      return;
    }

    loadRoom();
    // eslint-disable-next-line
  }, [token]);

  const submitUnlock = () => {
    setGateError(null);

    // Email gate: requesting entry sends a magic link to the invited inbox;
    // access is only granted when that link is clicked.
    if (gate === "email") {
      const email = gateEmail.trim();
      axios
        .post(`${serverUrl}/api/v3/room/${token}/unlock`, { email })
        .then(() => {
          setGateStage("sent");
        })
        .catch((_error) => {
          const code = _error.response?.data?.error;
          const messages = {
            too_many_attempts:
              "Too many attempts - please wait a few minutes and try again.",
            email_not_invited:
              "That email isn't on the invite list for this room. Ask the person who shared it to add you.",
            invalid_email: "Please enter a valid email address.",
            send_failed:
              "The access email couldn't be sent right now. Please try again later or contact the person who shared this room.",
          };
          setGateError(
            messages[code] || "Something went wrong - please try again.",
          );
        });
      return;
    }

    axios
      .post(`${serverUrl}/api/v3/room/${token}/unlock`, { password })
      .then((response) => {
        try {
          sessionStorage.setItem(unlockKey, response.data.unlock || "");
        } catch (e) {
          // Session-only fallback: reload still works within this call.
        }
        setPassword("");
        loadRoom();
      })
      .catch((_error) => {
        const code = _error.response?.data?.error;
        setGateError(
          code === "too_many_attempts"
            ? "Too many attempts - please wait a few minutes and try again."
            : "That passcode isn't right. Please try again.",
        );
      });
  };

  // Messages: fetch when the panel opens, poll lightly while it stays open.
  const loadMessages = useCallback(() => {
    axios
      .get(buyerUrl("/messages"), requestConfig)
      .then((response) => {
        setMessages(response.data.rows || []);
      })
      .catch(() => {});
    // eslint-disable-next-line
  }, [buyerUrl]);

  useEffect(() => {
    if (messagesOpen && room && (consented || isPreview)) {
      loadMessages();
      pollRef.current = setInterval(loadMessages, 20000);
      return () => clearInterval(pollRef.current);
    }
    return undefined;
    // eslint-disable-next-line
  }, [messagesOpen, room, consented]);

  // Curated activity feed - loaded lazily when the buyer opens the tab.
  const loadActivity = useCallback(() => {
    axios
      .get(buyerUrl("/activity"), requestConfig)
      .then((response) => setActivity(response.data))
      .catch(() => setActivity({ rows: [], pager: null }));
    // eslint-disable-next-line
  }, [buyerUrl]);

  // Loaded for the Dashboard, whose "Latest updates" cards summarise the
  // feed. activeTabKey is null until the buyer picks a tab, which means the
  // Dashboard.
  useEffect(() => {
    const onDashboard = activeTabKey === "dashboard" || !activeTabKey;
    if (onDashboard && room && (consented || isPreview)) {
      loadActivity();
    }
    // eslint-disable-next-line
  }, [activeTabKey, room, consented]);

  // `scope` lets a caller (the per-entity comment modal) post to a specific
  // content item without going through the shared composer's compose context.
  const sendMessage = (body, attachments = [], scope = null) => {
    if (!identity.name || !identity.email) {
      return Promise.reject(new Error("Please add your name and email first."));
    }

    try {
      localStorage.setItem(IDENTITY_KEY, JSON.stringify(identity));
    } catch (e) {
      // Storage unavailable - the message still sends.
    }

    const ctx = scope || composeContext;
    const payload = {
      author_name: identity.name,
      author_email: identity.email,
      body,
      attachments,
    };
    if (ctx?.kind === "request") {
      payload.kind = "request";
    }
    if (ctx?.content_type && ctx?.content_id) {
      payload.content_type = ctx.content_type;
      payload.content_id = ctx.content_id;
    }

    return axios
      .post(buyerUrl("/messages"), payload, {
        headers: { "Content-Type": "text/plain" },
      })
      .then((response) => {
        setMessages((prev) => [...prev, response.data]);
        setIdentityConfirmed(true);
        identifyVisitor(identity.email, identity.name);
        // Only the shared composer's context is one-shot; the modal manages
        // its own scope for the whole time it's open.
        if (!scope) {
          setComposeContext(null);
        }
      });
  };

  // Uploads one file to the identified-buyer endpoint; returns the attachment
  // descriptor ({fid, filename, mime, size, is_image}) for the composer.
  const uploadRoomFile = (file) => {
    if (!identity.email) {
      return Promise.reject(new Error("Add your email first."));
    }
    const form = new FormData();
    form.append("author_email", identity.email);
    form.append("file", file);
    return axios
      .post(`${serverUrl}/api/v3/room/${token}/files`, form)
      .then((response) => response.data);
  };

  // Token-scoped stream URL for a sent attachment.
  const attachmentUrl = (fid) =>
    `${serverUrl}/api/v3/room/${token}/files/${fid}`;

  const acceptConsent = () => {
    try {
      localStorage.setItem(consentKey, "1");
    } catch (e) {
      // Storage unavailable - consent still applies to this page view.
    }
    setConsented(true);
    // First recorded event doubles as the consent audit trail.
    track({ event_type: "room_opened", metadata: { consented: true } });
  };

  const openScopedComposer = (context) => {
    setComposeContext(context);
    setMessagesOpen(true);
    const target = document.getElementById("messages");
    if (target) {
      target.scrollIntoView({ behavior: "smooth" });
    }
  };

  // Per-entity discussion opens in its own modal, keyed to that content item.
  const openCommentModal = (item) => {
    setCommentModal({
      content_type: item.type,
      content_id: Number(item.id),
      title: item.title,
    });
  };
  const closeCommentModal = () => setCommentModal(null);

  // Saves the step form - creating when the draft has no id, editing the
  // named step when it does. Open to both sides from this screen: the buyer
  // posts their name/email (the same identity the thread captures), the
  // seller's preview session authenticates via the cookie.
  const saveStep = () => {
    const title = stepDraft.title.trim();
    if (!title || savingStep) {
      return;
    }
    const isEdit = !!stepDraft.id;
    if (!isEdit && !isPreview && (!identity.name || !identity.email)) {
      setStepError("Please add your name and email first.");
      return;
    }

    // A status-only editor sends only the status: anything else would be
    // refused, and there is nothing else they were allowed to change.
    const payload =
      stepMode === "status"
        ? { status: stepDraft.status, visitor_id: getVisitorId() }
        : {
            title,
            description: stepDraft.description.trim(),
            assignee: stepDraft.assignee,
            status: stepDraft.status,
            due: stepDraft.due ? moment(stepDraft.due).unix() : 0,
            visitor_id: getVisitorId(),
          };
    // Creating carries the buyer's identity so the plan records who added the
    // step; editing an existing one doesn't rewrite that attribution.
    if (!isEdit && !isPreview) {
      payload.author_name = identity.name;
      payload.author_email = identity.email;
      try {
        localStorage.setItem(IDENTITY_KEY, JSON.stringify(identity));
      } catch (e) {
        // Storage unavailable - the step still saves.
      }
    }

    setSavingStep(true);
    setStepError(null);
    axios
      .post(
        buyerUrl(isEdit ? `/milestones/${stepDraft.id}` : "/milestones"),
        payload,
        { headers: { "Content-Type": "text/plain" }, ...requestConfig },
      )
      .then((response) => {
        setMilestones(response.data.milestones || []);
        setStepDraft(EMPTY_STEP);
        setStepFormOpen(false);
        if (!isEdit && !isPreview) {
          setIdentityConfirmed(true);
          identifyVisitor(identity.email, identity.name);
        }
      })
      .catch(() => {
        setStepError("That step couldn't be saved - please try again.");
      })
      .finally(() => setSavingStep(false));
  };

  // Opens the step dialog: pass a milestone to edit it, null to add one.
  const openStepForm = (milestone) => {
    setStepMode(milestone ? stepPermission(milestone) : "full");
    setStepDraft(
      milestone
        ? {
            id: milestone.id,
            title: milestone.title || "",
            description: milestone.description || "",
            due: milestone.due
              ? moment.unix(milestone.due).format("YYYY-MM-DD")
              : "",
            assignee: milestone.assignee === "buyer" ? "buyer" : "seller",
            status: STEP_STATUSES[milestone.status]
              ? milestone.status
              : "pending",
          }
        : { ...EMPTY_STEP, assignee: isPreview ? "seller" : "buyer" },
    );
    setStepError(null);
    setStepFormOpen(true);
  };

  // What this viewer may change on a step. Mirrors the rule the buyer API
  // enforces (BuyerRoomController::buyerEditDenial) so the table only offers
  // what the server will actually accept - the server is the authority.
  //   "full"   - every field
  //   "status" - progress only; the seller's wording stays the seller's
  // Every row is actionable: a buyer can always say where a step has got to,
  // whoever it belongs to.
  const stepPermission = (milestone) => {
    if (isPreview || milestone.created_by === "buyer") {
      return "full";
    }
    return "status";
  };

  // Client-side column sort for the plan table. Clicking the active column
  // flips the direction; a new column starts ascending.
  const togglePlanSort = (key) =>
    setPlanSort((prev) =>
      prev.key === key ? { key, asc: !prev.asc } : { key, asc: true },
    );

  const openContentItem = (item) => {
    if (newTabRoutes[item.type]) {
      track({
        event_type: item.type === "link" ? "link_clicked" : "content_viewed",
        content_type: item.type,
        content_id: Number(item.id),
      });
      window.open(
        `${window.location.origin}${newTabRoutes[item.type](item)}`,
        "_blank",
      );
      return;
    }

    switch (item.type) {
      case "link":
        track({
          event_type: "link_clicked",
          content_type: item.type,
          content_id: Number(item.id),
        });
        if (item.url) {
          window.open(item.url, "_blank");
        }
        break;

      case "document":
      case "video":
      case "podcast":
      case "gallery":
        startContentTimer(item);
        // Merge in the published-dir URLs (fileUrl / images[].src); the viewer
        // falls back to token_share for anything left unresolved.
        setOpenItem({ ...item, ...resolvedAssets[`${item.type}:${item.id}`] });
        break;

      default:
        break;
    }
  };

  const closeViewer = () => {
    stopContentTimer();
    setOpenItem(null);
  };

  if (isLoading) {
    return (
      <main className="buyer-room screen">
        <CompanyappLoader />
      </main>
    );
  }

  if (gate) {
    const isEmailGate = gate === "email";
    const linkSent = isEmailGate && gateStage === "sent";

    return (
      <main className="buyer-room buyer-room--gate screen">
        <Container>
          <div className="buyer-room__gate-card">
            <LockIcon className="buyer-room__gate-icon" />
            <Typography owner="client" variant="h5" className="mb-2">
              {linkSent
                ? "Check your email"
                : isEmailGate
                  ? "This room is for invited people"
                  : "This room is passcode protected"}
            </Typography>
            <Typography owner="client" variant="body2" className="mb-3">
              {linkSent
                ? `We've sent a secure access link to ${gateEmail.trim()}. Open it on this device to enter the room. The link is valid for 24 hours.`
                : isEmailGate
                  ? "Enter the email address the room was shared with and we'll send you a secure access link."
                  : "Enter the passcode you received from the person who shared this room with you."}
            </Typography>

            {!linkSent && isEmailGate && (
              <TextField
                className="w-100 mb-2"
                type="email"
                size="small"
                label="Email address"
                value={gateEmail}
                autoFocus
                onChange={(e) => setGateEmail(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    submitUnlock();
                  }
                }}
              />
            )}
            {!isEmailGate && (
              <TextField
                className="w-100 mb-2"
                type="password"
                size="small"
                label="Passcode"
                value={password}
                autoFocus
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    submitUnlock();
                  }
                }}
              />
            )}

            {gateError && (
              <Typography
                owner="client"
                variant="body2"
                color="error"
                className="d-block mb-2"
              >
                {gateError}
              </Typography>
            )}

            {linkSent ? (
              <>
                <Button
                  variant="contained"
                  className="w-100 buyer-room__btn mb-2"
                  onClick={submitUnlock}
                >
                  <Typography owner="client" variant="button">
                    Re-send the link
                  </Typography>
                </Button>
                <Button
                  variant="outlined"
                  className="w-100 buyer-room__btn buyer-room__btn--outlined"
                  onClick={() => {
                    setGateStage("input");
                    setGateError(null);
                  }}
                >
                  <Typography owner="client" variant="button">
                    Use a different email
                  </Typography>
                </Button>
              </>
            ) : (
              <Button
                variant="contained"
                className="w-100 buyer-room__btn"
                disabled={isEmailGate ? !gateEmail.trim() : !password}
                onClick={submitUnlock}
              >
                <Typography owner="client" variant="button">
                  {isEmailGate ? "Send access link" : "Enter room"}
                </Typography>
              </Button>
            )}
          </div>
        </Container>
      </main>
    );
  }

  if (error) {
    const copy = errorCopy[error] || errorCopy.network;
    return (
      <main className="buyer-room buyer-room--error screen">
        <Container>
          <div className="buyer-room__error-card">
            <Typography owner="client" variant="h4" className="mb-2">
              {copy.title}
            </Typography>
            <Typography owner="client" variant="body1">
              {copy.body}
            </Typography>
          </div>
        </Container>
      </main>
    );
  }

  // Past this point every branch reads room.* - the loading / gate / error
  // early-returns above should always cover a null room, but guard against
  // any transient state race so a missing room can never crash the page.
  if (!room) {
    return (
      <main className="buyer-room screen">
        <CompanyappLoader />
      </main>
    );
  }

  const accent = room.branding?.colour || "#0e6ba8";
  // Two brand marks: the client's (masthead right) and the seller's own
  // (masthead left). Either may be unset - the seller's falls back to the
  // app logo, the client's to their company name.
  const logo = room.branding?.logo;
  const companyLogo = room.branding?.company_logo;
  const doneCount = milestones.filter((m) => m.status === "done").length;
  // Plan rows in the order the table's active column asks for. Undated steps
  // sort last whichever way the date column points, so they never push the
  // scheduled work down the list.
  const sortedMilestones = [...milestones].sort((a, b) => {
    const direction = planSort.asc ? 1 : -1;
    if (planSort.key === "status") {
      const rank = (m) =>
        (STEP_STATUSES[m.status] || STEP_STATUSES.pending).order;
      return (rank(a) - rank(b)) * direction;
    }
    if (planSort.key === "owner") {
      const rank = (m) => (m.assignee === "buyer" ? 0 : 1);
      return (rank(a) - rank(b)) * direction;
    }
    if (!a.due || !b.due) {
      return (a.due ? 0 : 1) - (b.due ? 0 : 1);
    }
    return (a.due - b.due) * direction;
  });
  const sectionsWithItems = (room.sections || []).filter(
    (section) => (section.items || []).length > 0,
  );

  // The tab bar is fixed: Dashboard (branding + intro) first and the default,
  // then Resources, which holds every content section the seller has built -
  // each section keeps its own heading and card grid, stacked in that one tab.
  // Action Plan, Activity and Messages follow.
  const tabs = [
    { key: "dashboard", label: "Dashboard", type: "dashboard" },
    { key: "resources", label: "Resources", type: "resources" },
  ];
  // Always present, even when empty: both sides add their own steps from
  // here, so there has to be somewhere to add the first one.
  tabs.push({ key: "milestones", label: "Action Plan", type: "milestones" });
  // No Activity tab: the feed is still fetched, but it surfaces through the
  // Dashboard's "Latest updates" cards rather than as a log of its own. The
  // renderer below is kept, so switching it back on is one push() here.
  // The conversation is always its own tab (no separate sidebar).
  tabs.push({ key: "messages", label: "Messages", type: "messages" });
  const activeTab = tabs.find((tab) => tab.key === activeTabKey) || tabs[0];
  const showTabs = tabs.length > 1;

  const showIdentityForm = !identityConfirmed;

  // "Latest updates": one card per other tab, each showing the three most
  // recent things that happened there.
  //
  // The activity feed alone isn't enough for this. item_added is only written
  // when a room is *updated* (SalesRoom::postSave returns early on insert), so
  // content added while the room was being built carries no event at all - a
  // fully stocked room can show a single "added" row. So each card takes what
  // the feed knows about first, then tops up from the tab's own current
  // contents, and every card fills to three lines wherever there are three
  // things to show.
  const UPDATE_LINES = 3;
  const activityRows = activity?.rows || [];
  const since = (ts) => (ts ? moment.unix(ts).fromNow() : "");
  const asText = (html) =>
    (html || "")
      .replace(/<[^>]*>/g, " ")
      .replace(/\s+/g, " ")
      .trim();

  // Feed rows for one tab, newest first.
  // Feed rows for one tab, newest first and one row per subject. The feed
  // records every change, so a step that was started, finished and reopened
  // is three milestone_updated rows naming the same step - only the newest
  // belongs in a "latest" list.
  const feedLines = (events) => {
    const seen = new Set();
    return activityRows
      .filter((row) => events.includes(row.event))
      .map((row) => ({
        key: `feed-${row._id ?? `${row.event}-${row.ts}`}`,
        dedupe: (row.content_title || "").toLowerCase(),
        title: row.content_title || row.snippet || "Update",
        meta: [row.actor, since(row.ts)].filter(Boolean).join(" · "),
      }))
      .filter((line) => {
        if (!line.dedupe || seen.has(line.dedupe)) {
          return !line.dedupe;
        }
        seen.add(line.dedupe);
        return true;
      });
  };

  // Feed rows first, then state-derived lines for anything not already named.
  const fill = (fromFeed, fromState) => {
    const seen = new Set(fromFeed.map((line) => line.dedupe).filter(Boolean));
    const topUp = fromState.filter((line) => {
      if (!line.dedupe || seen.has(line.dedupe)) {
        return !line.dedupe;
      }
      seen.add(line.dedupe);
      return true;
    });
    return [...fromFeed, ...topUp].slice(0, UPDATE_LINES);
  };

  const itemCount = sectionsWithItems.reduce(
    (total, section) => total + section.items.length,
    0,
  );

  // Every content item, in the order the seller arranged the room.
  const resourceLines = fill(
    feedLines(["item_added"]).slice(0, UPDATE_LINES),
    sectionsWithItems.flatMap((section) =>
      section.items.map((item) => ({
        key: `item-${item.type}-${item.id}`,
        dedupe: (item.title || "").toLowerCase(),
        title: item.title,
        meta: [section.title, (typeMeta[item.type] || typeMeta.document).label]
          .filter(Boolean)
          .join(" · "),
      })),
    ),
  );

  // Open steps, soonest due first; undated ones last.
  const openSteps = milestones
    .filter((step) => step.status !== "done")
    .sort((a, b) => (a.due || Infinity) - (b.due || Infinity));
  const planLines = fill(
    feedLines(["milestone_added", "milestone_updated"]).slice(0, UPDATE_LINES),
    openSteps.map((step) => ({
      key: `step-${step.id}`,
      dedupe: (step.title || "").toLowerCase(),
      title: step.title,
      meta: [
        step.due ? `Due ${moment.unix(step.due).format("D MMM YYYY")}` : null,
        (STEP_STATUSES[step.status] || STEP_STATUSES.pending).label,
      ]
        .filter(Boolean)
        .join(" · "),
    })),
  );

  // The thread is already loaded and in order, so it beats the feed here.
  const messageLines = [...messages]
    .slice(-UPDATE_LINES)
    .reverse()
    .map((message) => ({
      key: `msg-${message.id}`,
      title: asText(message.body) || "Sent an attachment",
      meta: [message.author_name || "Someone", since(message.created)]
        .filter(Boolean)
        .join(" · "),
    }));

  // `show` drops a card whose tab is empty entirely - an empty card says
  // nothing worth the space. A tab that has content but nothing recent to
  // report (every step done, say) keeps its card and shows the fallback.
  const updateCards = [
    {
      key: "resources",
      label: "Resources",
      tab: "resources",
      show: itemCount > 0,
      lines: resourceLines,
      fallback: `${itemCount} item${itemCount === 1 ? "" : "s"} shared.`,
    },
    {
      key: "milestones",
      label: "Action plan",
      tab: "milestones",
      show: milestones.length > 0,
      lines: planLines,
      fallback: `All ${milestones.length} step${
        milestones.length === 1 ? "" : "s"
      } complete.`,
    },
    {
      key: "messages",
      label: "Messages",
      tab: "messages",
      show: messages.length > 0,
      lines: messageLines,
      fallback: `${messages.length} message${
        messages.length === 1 ? "" : "s"
      } in the conversation.`,
    },
  ].filter((card) => card.show);


  // The general conversation vs. comments tied to a specific content item.
  // Content requests (no content_id) stay in the general conversation.
  const generalMessages = messages.filter(
    (m) => !m.content_type || !m.content_id,
  );
  const itemCommentGroups = Object.values(
    messages
      .filter((m) => m.content_type && m.content_id)
      .reduce((acc, m) => {
        const key = `${m.content_type}:${m.content_id}`;
        if (!acc[key]) {
          acc[key] = {
            key,
            content_type: m.content_type,
            content_id: m.content_id,
            title: m.content_title || `${m.content_type} ${m.content_id}`,
            messages: [],
          };
        }
        acc[key].messages.push(m);
        return acc;
      }, {}),
  );

  // Per-item comment counts for the content cards: the room payload seeds
  // them (available before the thread loads); loaded messages override with
  // live counts so new posts increment immediately.
  const commentCounts = { ...(room.comment_counts || {}) };
  itemCommentGroups.forEach((group) => {
    commentCounts[group.key] = group.messages.length;
  });

  const introVideo = room.intro_video;
  const introVideoUrl =
    introVideo && introVideo.filename
      ? `${serverUrl}/shared/files/${encodeURIComponent(introVideo.filename)}?access_token=${introVideo.file_access_token}`
      : null;
  // Either an uploaded file or an oEmbed URL is enough to show the player;
  // without one the welcome copy takes the full width.
  const hasIntroVideo = !!(
    introVideoUrl || introVideo?.field_media_oembed_video
  );

  // Tracking disclosure - blocks all content until the buyer agrees.
  // Seller previews skip it (nothing is tracked in preview anyway).
  if (!consented && !room.preview) {
    return (
      <main
        className="buyer-room buyer-room--gate screen"
        style={{ "--dsr-accent": accent }}
      >
        <Container>
          <div className="buyer-room__gate-card buyer-room__gate-card--wide">
            {logo && (
              <img
                className="buyer-room__consent-logo"
                src={logo}
                alt={room.company || room.title}
              />
            )}
            <Typography owner="client" variant="h5" className="mb-3">
              Before you continue
            </Typography>
            <Typography
              owner="client"
              variant="body2"
              className="mb-3 text-start"
            >
              This room has been shared with you by{" "}
              {room.seller?.name || "the sales team"}
              {room.company ? ` for ${room.company}` : ""}. To help them assist
              you, your activity here - the content you view, time spent,
              downloads, and any messages you send - is recorded and shared with
              them.
            </Typography>
            <Typography
              owner="client"
              variant="body2"
              className="mb-4 text-start"
            >
              By continuing, you agree to this activity being tracked and to the
              terms and conditions of use of this room.
            </Typography>
            <Button
              variant="contained"
              className="buyer-room__btn buyer-room__consent-btn"
              onClick={acceptConsent}
            >
              <Typography owner="client" variant="button">
                Agree and continue
              </Typography>
            </Button>
            <Typography
              owner="client"
              variant="caption"
              className="d-block mt-3"
            >
              If you don't agree, simply close this page - no activity has been
              recorded.
            </Typography>
          </div>
        </Container>
      </main>
    );
  }

  // The identity capture form, shown in any composer until the buyer has
  // posted with a name + email. Shared by the main composer and the modal.
  const identityFormSlot = showIdentityForm ? (
    <div className="buyer-room__identity mb-3">
      <Typography owner="client" variant="body2" className="d-block mb-2">
        Add your details so the team knows who's writing. Your name and email
        are shared with the participants of this room.
      </Typography>
      <div className="buyer-room__identity-fields">
        <TextField
          size="small"
          label="Your name"
          value={identity.name}
          onChange={(e) => setIdentity({ ...identity, name: e.target.value })}
        />
        <TextField
          size="small"
          label="Your email"
          type="email"
          value={identity.email}
          onChange={(e) => setIdentity({ ...identity, email: e.target.value })}
        />
      </div>
    </div>
  ) : null;

  // The shared conversation, rendered once - either in the sticky aside (wide
  // screens) or inside the Messages tab (narrow screens). Per-entity comments
  // no longer live here; each item's discussion opens in its own modal.
  const conversationInner = (
    <section className="buyer-room__messages mb-4" id="messages">
      <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
        <Typography
          owner="client"
          variant="h5"
          className="buyer-room__section-title mb-0"
        >
          Messages
        </Typography>
        {!messagesOpen && (
          <Button
            size="small"
            variant="outlined"
            className="buyer-room__btn buyer-room__btn--outlined"
            onClick={() => setMessagesOpen(true)}
          >
            <Typography owner="client" variant="button">
              Open conversation
            </Typography>
          </Button>
        )}
        {!room.preview && (
          <Button
            size="small"
            variant="outlined"
            className="buyer-room__btn buyer-room__btn--outlined"
            onClick={() =>
              openScopedComposer({
                kind: "request",
                label: "Content request - tell us what you need",
              })
            }
          >
            <Typography owner="client" variant="button">
              Request content
            </Typography>
          </Button>
        )}
      </div>
      {messagesOpen && (
        <RoomThread
          messages={generalMessages}
          onSend={sendMessage}
          canPost={!room.preview}
          mine="buyer"
          uploadFile={uploadRoomFile}
          attachmentUrl={attachmentUrl}
          canUpload={!!identity.email && !room.preview}
          uploadHint={
            room.preview
              ? "Attachments are disabled in preview."
              : "Add your name and email above to attach files."
          }
          scope={
            composeContext
              ? {
                  label: composeContext.label,
                  onClear: () => setComposeContext(null),
                }
              : null
          }
          emptyText="No messages yet - ask a question and the team will be notified."
          identitySlot={identityFormSlot}
        />
      )}
    </section>
  );

  // Messages tied to the entity item whose comment modal is open.
  const modalMessages = commentModal
    ? messages.filter(
        (m) =>
          m.content_type === commentModal.content_type &&
          Number(m.content_id) === commentModal.content_id,
      )
    : [];

  // Renders one activity row as a human sentence + icon + relative time.
  const renderActivityItem = (item, index) => {
    const when = item.ts ? moment.unix(item.ts).fromNow() : "";
    const actor = item.actor || (item.is_seller ? "The team" : "Someone");
    const title = item.content_title;
    let Icon = ChatIcon;
    let text;
    switch (item.event) {
      case "comment":
        text = title
          ? `${actor} commented on "${title}"`
          : `${actor} posted in the discussion`;
        break;
      case "request":
        text = `${actor} requested content`;
        break;
      case "item_added":
        Icon = AddCircleIcon;
        text = title ? `${actor} added "${title}"` : `${actor} added an item`;
        break;
      case "item_removed":
        Icon = RemoveCircleIcon;
        text = title
          ? `${actor} removed "${title}"`
          : `${actor} removed an item`;
        break;
      case "milestone_updated":
        Icon = item.milestone_status === "done" ? TaskDoneIcon : ChatIcon;
        text =
          item.milestone_status === "done"
            ? `${actor} completed "${title || "a step"}"`
            : `${actor} reopened "${title || "a step"}"`;
        break;
      case "milestone_added":
        Icon = AddCircleIcon;
        text = `${actor} added "${title || "a step"}" to the action plan`;
        break;
      default:
        text = `${actor} updated the room`;
    }
    return (
      <li className="buyer-room__activity-item" key={`activity-${index}`}>
        <span
          className={`buyer-room__activity-icon buyer-room__activity-icon--${item.event}`}
        >
          <Icon fontSize="small" />
        </span>
        <div className="buyer-room__activity-body">
          <Typography
            owner="client"
            variant="body2"
            className="buyer-room__activity-text"
          >
            {text}
          </Typography>
          {item.snippet && (
            <Typography
              owner="client"
              variant="body2"
              className="buyer-room__activity-snippet"
            >
              {item.snippet}
            </Typography>
          )}
          <Typography
            owner="client"
            variant="caption"
            className="buyer-room__activity-time"
          >
            {when}
          </Typography>
        </div>
      </li>
    );
  };

  return (
    <main className="buyer-room screen" style={{ "--dsr-accent": accent }}>
      {room.preview && (
        <div className="buyer-room__preview-banner">
          <Typography owner="client" variant="body2">
            Preview - this is what buyers will see. Visits aren't tracked, and
            the room stays hidden until it's set to Active.
          </Typography>
        </div>
      )}

      <header className="buyer-room__masthead">
        <div className="buyer-room__masthead-inner">
          <div className="buyer-room__masthead-brand buyer-room__masthead-brand--seller">
            {companyLogo ? (
              <img src={companyLogo} alt={room.seller?.name || "Company"} />
            ) : (
              <AppLogo />
            )}
          </div>
          <div className="buyer-room__masthead-centre">
            <Typography
              owner="client"
              variant="h5"
              className="buyer-room__masthead-title"
            >
              {room.title}
            </Typography>
            {showTabs && (
              <div className="buyer-room__tabs" role="tablist">
                {tabs.map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    role="tab"
                    aria-selected={activeTab?.key === tab.key}
                    className={`buyer-room__tab ${
                      activeTab?.key === tab.key
                        ? "buyer-room__tab--active"
                        : ""
                    }`}
                    onClick={() => setActiveTabKey(tab.key)}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="buyer-room__masthead-brand buyer-room__masthead-brand--buyer">
            {logo && <img src={logo} alt={room.company || room.title} />}
          </div>
        </div>
      </header>

      <Container className="buyer-room__content">
        <Row className="buyer-room__layout gx-0">
          <Col xs={12} className="buyer-room__main-col">
            {activeTab?.type === "dashboard" && (
              <>
                <section className="buyer-room__welcome mb-5">
                  <Typography
                    owner="client"
                    variant="h5"
                    className="buyer-room__section-title"
                  >
                    {room.dashboard_title || DEFAULT_DASHBOARD_TITLE}
                  </Typography>
                  <Row className="g-4 align-items-stretch">
                    <Col xs={12} lg={hasIntroVideo ? 6 : 12} className="d-flex">
                      <div className="buyer-room__welcome-card">
                        {room.description ? (
                          <div
                            className="buyer-room__lead"
                            dangerouslySetInnerHTML={{
                              __html: room.description,
                            }}
                          />
                        ) : (
                          <Typography
                            owner="client"
                            variant="body1"
                            className="buyer-room__lead"
                          >
                            This room was prepared
                            {room.company ? ` for ${room.company}` : ""}
                            {room.seller?.name ? ` by ${room.seller.name}` : ""}
                            . Everything shared with you lives in the tabs
                            above.
                          </Typography>
                        )}
                      </div>
                    </Col>
                    {hasIntroVideo && (
                      <Col xs={12} lg={6}>
                        <div className="buyer-room__intro-card">
                          <div className="buyer-room__intro-video">
                            {introVideoUrl ? (
                              <video controls preload="metadata">
                                <source src={introVideoUrl} />
                              </video>
                            ) : (
                              <iframe
                                src={introVideo.field_media_oembed_video}
                                title="Introduction"
                                allowFullScreen
                              ></iframe>
                            )}
                          </div>
                          {introVideo?.label && (
                            <Typography
                              owner="client"
                              variant="body2"
                              className="buyer-room__intro-label"
                            >
                              {introVideo.label}
                            </Typography>
                          )}
                        </div>
                      </Col>
                    )}
                  </Row>
                </section>

                <RoomUpdateCards
                  cards={updateCards}
                  owner="client"
                  onOpen={(card) => setActiveTabKey(card.tab)}
                />
              </>
            )}
            {(activeTab?.type === "resources" ? sectionsWithItems : []).map(
              (section) => (
                <section className="buyer-room__section" key={section.id}>
                  {section.title && (
                    <Typography
                      owner="client"
                      variant="h5"
                      className="buyer-room__section-title"
                    >
                      {section.title}
                    </Typography>
                  )}
                  {section.description && (
                    <Typography
                      owner="client"
                      variant="body2"
                      className="buyer-room__section-description"
                    >
                      {section.description}
                    </Typography>
                  )}
                  <Row className="gy-3">
                    {section.items.map((item) => {
                      const meta = typeMeta[item.type] || typeMeta.document;
                      const TypeIcon = meta.icon;
                      const opensNewTab =
                        !!newTabRoutes[item.type] || item.type === "link";
                      const commentCount =
                        commentCounts[`${item.type}:${item.id}`] || 0;
                      // Prefer the signed published-dir thumbnail; fall back to the
                      // token_share/file-server thumbnail from the room payload.
                      const thumbSrc =
                        resolvedAssets[`${item.type}:${item.id}`]?.thumbnail ||
                        (item.thumbnail
                          ? item.thumbnail.indexOf("http") === 0
                            ? item.thumbnail
                            : `${serverUrl}${item.thumbnail}`
                          : null);
                      return (
                        <Col
                          xs={12}
                          sm={6}
                          lg={3}
                          key={`item-${item.type}-${item.id}`}
                        >
                          <div
                            className="buyer-room__card"
                            role="button"
                            tabIndex={0}
                            onClick={() => openContentItem(item)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                openContentItem(item);
                              }
                            }}
                          >
                            <div className="buyer-room__card-thumbnail">
                              {thumbSrc ? (
                                <img src={thumbSrc} alt={item.title} />
                              ) : (
                                <TypeIcon className="buyer-room__card-icon" />
                              )}
                              <span className="buyer-room__card-type-chip">
                                {meta.label}
                              </span>
                            </div>
                            <div className="buyer-room__card-body">
                              <Typography
                                owner="client"
                                variant="body1"
                                className="buyer-room__card-title"
                              >
                                {item.title}
                              </Typography>
                              <Typography
                                owner="client"
                                variant="body2"
                                className="buyer-room__card-summary"
                              >
                                {item.summary}
                              </Typography>
                              <div className="buyer-room__card-actions">
                                <Typography
                                  owner="client"
                                  variant="caption"
                                  className="buyer-room__card-type"
                                >
                                  {item.type === "event" &&
                                  item.field_start_date
                                    ? moment(item.field_start_date).format(
                                        "DD MMM YYYY",
                                      )
                                    : ""}
                                  {opensNewTab && (
                                    <OpenNewIcon className="buyer-room__card-open" />
                                  )}
                                </Typography>
                                {(!room.preview || commentCount > 0) && (
                                  <IconButton
                                    size="small"
                                    className="buyer-room__card-discuss"
                                    title={
                                      commentCount > 0
                                        ? `${commentCount} comment${
                                            commentCount === 1 ? "" : "s"
                                          }${room.preview ? "" : " · add yours"}`
                                        : `Discuss "${item.title}"`
                                    }
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openCommentModal(item);
                                    }}
                                  >
                                    <Badge
                                      badgeContent={commentCount}
                                      overlap="circular"
                                      className="buyer-room__card-badge"
                                    >
                                      <ChatIcon fontSize="small" />
                                    </Badge>
                                  </IconButton>
                                )}
                              </div>
                            </div>
                          </div>
                        </Col>
                      );
                    })}
                  </Row>
                </section>
              ),
            )}

            {activeTab?.type === "resources" &&
              sectionsWithItems.length === 0 && (
                <Typography owner="client" variant="body1">
                  No content has been shared in this room yet.
                </Typography>
              )}

            {activeTab?.type === "milestones" && (
              <section className="buyer-room__plan mb-5">
                <Typography
                  owner="client"
                  variant="h5"
                  className="buyer-room__section-title"
                >
                  Mutual Action Plan
                </Typography>

                <div className="buyer-room__plan-progress-card">
                  <div className="buyer-room__progress">
                    <div
                      className="buyer-room__progress-bar"
                      style={{
                        width: `${
                          milestones.length
                            ? Math.round((doneCount / milestones.length) * 100)
                            : 0
                        }%`,
                      }}
                    />
                  </div>
                  <Typography
                    owner="client"
                    variant="caption"
                    className="buyer-room__progress-label"
                  >
                    {doneCount} of {milestones.length} steps complete
                  </Typography>
                </div>

                {milestones.length === 0 ? (
                  <Typography
                    owner="client"
                    variant="body2"
                    className="buyer-room__plan-empty"
                  >
                    No steps yet. Use the button below to add the first one -
                    everyone in this room sees the same plan.
                  </Typography>
                ) : (
                  <div className="buyer-room__plan-table">
                    <div className="buyer-room__plan-head" role="row">
                      {PLAN_COLUMNS.map((column) => (
                        <div
                          className={`buyer-room__plan-cell buyer-room__plan-cell--${column.key}`}
                          role="columnheader"
                          key={column.key}
                        >
                          {column.sortable ? (
                            <button
                              type="button"
                              className={`buyer-room__plan-sort ${
                                planSort.key === column.key
                                  ? "buyer-room__plan-sort--active"
                                  : ""
                              }`}
                              onClick={() => togglePlanSort(column.key)}
                            >
                              {column.label}
                              <SortIcon
                                fontSize="inherit"
                                className={
                                  planSort.key === column.key && !planSort.asc
                                    ? "buyer-room__plan-caret--up"
                                    : ""
                                }
                              />
                            </button>
                          ) : (
                            column.label
                          )}
                        </div>
                      ))}
                    </div>

                    {sortedMilestones.map((milestone) => {
                      const status =
                        STEP_STATUSES[milestone.status] ||
                        STEP_STATUSES.pending;
                      const permission = stepPermission(milestone);
                      return (
                        <div
                          className="buyer-room__plan-row"
                          role="row"
                          key={milestone.id}
                        >
                          <div className="buyer-room__plan-cell buyer-room__plan-cell--due">
                            {milestone.due
                              ? moment.unix(milestone.due).format("D MMMM YYYY")
                              : "-"}
                          </div>
                          <div className="buyer-room__plan-cell buyer-room__plan-cell--status">
                            <span
                              className={`buyer-room__plan-chip buyer-room__plan-chip--${status.key}`}
                            >
                              {status.label}
                            </span>
                          </div>
                          <div className="buyer-room__plan-cell buyer-room__plan-cell--action">
                            {milestone.title}
                          </div>
                          <div className="buyer-room__plan-cell buyer-room__plan-cell--comments">
                            {milestone.description}
                          </div>
                          <div className="buyer-room__plan-cell buyer-room__plan-cell--owner">
                            {/* The owning side is shown as its brand mark: the
                            client's logo for the buyer, the seller's company
                            logo for us (the app logo when none is set). */}
                            {milestone.assignee === "buyer" ? (
                              logo ? (
                                <img src={logo} alt={room.company || "Buyer"} />
                              ) : (
                                <span className="buyer-room__plan-owner-text">
                                  {room.company || "Buyer"}
                                </span>
                              )
                            ) : companyLogo ? (
                              <img
                                src={companyLogo}
                                alt={room.seller?.name || "Company"}
                              />
                            ) : (
                              <AppLogo />
                            )}
                          </div>
                          <div className="buyer-room__plan-cell buyer-room__plan-cell--edit">
                            <IconButton
                              size="small"
                              title={
                                permission === "status"
                                  ? `Update progress on "${milestone.title}"`
                                  : `Edit "${milestone.title}"`
                              }
                              onClick={() => openStepForm(milestone)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Either side adds or edits steps from here: the buyer for their
                own to-dos, the seller while previewing the room. */}
                <Tooltip title="Add a step">
                  <button
                    type="button"
                    className="buyer-room__plan-fab"
                    onClick={() => openStepForm(null)}
                  >
                    <AddIcon />
                  </button>
                </Tooltip>
              </section>
            )}
            {activeTab?.type === "activity" && (
              <section className="buyer-room__activity mb-5">
                {!showTabs && (
                  <Typography
                    owner="client"
                    variant="h5"
                    className="buyer-room__section-title"
                  >
                    Activity
                  </Typography>
                )}
                {activity === null ? (
                  <div className="buyer-room__activity-loading">
                    <CircularProgress size={22} />
                  </div>
                ) : (activity.rows || []).length === 0 ? (
                  <Typography owner="client" variant="body2">
                    No activity yet. Comments, content updates and action plan
                    changes will show up here.
                  </Typography>
                ) : (
                  <ul className="buyer-room__activity-list">
                    {activity.rows.map((item, index) =>
                      renderActivityItem(item, index),
                    )}
                  </ul>
                )}
              </section>
            )}
            {activeTab?.type === "messages" && conversationInner}
          </Col>
        </Row>
      </Container>

      <footer className="buyer-room__footer">
        <Container className="py-4">
          <Typography owner="client" variant="caption" className="d-block mb-3">
            Shared privately with you
            {room.company ? ` and ${room.company}` : ""}. Interactions in this
            room (content views and messages) are visible to{" "}
            {room.seller?.name || "the sales team"} to help them assist you.
          </Typography>
          <a
            className="buyer-room__powered-by"
            href="https://companyapp.net"
            target="_blank"
            rel="noreferrer"
          >
            <Typography owner="client" variant="caption">
              Powered by
            </Typography>
            <img src={caLogo} alt="Companyapp" />
            <Typography owner="client" variant="caption" weight="medium">
              Companyapp
            </Typography>
          </a>
        </Container>
      </footer>

      {openItem && (
        <RoomItemViewer item={openItem} onClose={closeViewer} track={track} />
      )}

      {/* Add / edit one action-plan step. The same dialog serves both: a
          draft with an id edits that step, without one creates a step. */}
      {stepFormOpen && (
        <Dialog
          open
          onClose={() => !savingStep && setStepFormOpen(false)}
          fullWidth
          maxWidth="sm"
          className="buyer-room__step-dialog"
          PaperProps={{ style: { "--dsr-accent": accent } }}
        >
          <div className="buyer-room__comment-modal-head">
            <Typography
              owner="client"
              variant="h6"
              className="buyer-room__comment-modal-title"
            >
              {!stepDraft.id
                ? "Add a step"
                : stepMode === "status"
                  ? "Update progress"
                  : "Edit step"}
            </Typography>
            <IconButton
              size="small"
              disabled={savingStep}
              onClick={() => setStepFormOpen(false)}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </div>
          <DialogContent className="buyer-room__step-dialog-body">
            {!stepDraft.id && !isPreview && identityFormSlot}
            {stepMode === "status" && (
              <Typography
                owner="client"
                variant="body2"
                className="buyer-room__step-note mb-3"
              >
                This step was set by the team, so its wording is theirs to
                change - but you can always say where it has got to. Ask in
                Messages if the details need updating.
              </Typography>
            )}
            <TextField
              size="small"
              fullWidth
              autoFocus={stepMode !== "status"}
              disabled={stepMode === "status"}
              label="Action"
              value={stepDraft.title}
              inputProps={{ maxLength: 255 }}
              onChange={(e) =>
                setStepDraft({ ...stepDraft, title: e.target.value })
              }
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  saveStep();
                }
              }}
            />
            <TextField
              size="small"
              fullWidth
              multiline
              minRows={2}
              className="mt-3"
              disabled={stepMode === "status"}
              label="Comments"
              value={stepDraft.description}
              inputProps={{ maxLength: 1000 }}
              onChange={(e) =>
                setStepDraft({ ...stepDraft, description: e.target.value })
              }
            />
            <div className="buyer-room__step-form-row mt-3">
              <TextField
                size="small"
                type="date"
                disabled={stepMode === "status"}
                label="Due date"
                InputLabelProps={{ shrink: true }}
                value={stepDraft.due}
                onChange={(e) =>
                  setStepDraft({ ...stepDraft, due: e.target.value })
                }
              />
              <TextField
                size="small"
                select
                label="Status"
                SelectProps={{ native: true }}
                InputLabelProps={{ shrink: true }}
                value={stepDraft.status}
                onChange={(e) =>
                  setStepDraft({ ...stepDraft, status: e.target.value })
                }
              >
                {Object.values(STEP_STATUSES).map((status) => (
                  <option value={status.key} key={status.key}>
                    {status.label}
                  </option>
                ))}
              </TextField>
              <TextField
                size="small"
                select
                disabled={stepMode === "status"}
                label="Owner"
                SelectProps={{ native: true }}
                InputLabelProps={{ shrink: true }}
                value={stepDraft.assignee}
                onChange={(e) =>
                  setStepDraft({ ...stepDraft, assignee: e.target.value })
                }
              >
                <option value="buyer">{room.company || "The buyer"}</option>
                <option value="seller">
                  {room.seller?.name ? "Us" : "The team"}
                </option>
              </TextField>
            </div>
            {stepError && (
              <Typography
                owner="client"
                variant="caption"
                className="buyer-room__step-error"
              >
                {stepError}
              </Typography>
            )}
            <div className="buyer-room__step-form-actions mt-3">
              <Button
                variant="contained"
                size="small"
                disabled={!stepDraft.title.trim() || savingStep}
                onClick={saveStep}
              >
                {savingStep ? "Saving..." : stepDraft.id ? "Save" : "Add step"}
              </Button>
              <Button
                variant="text"
                size="small"
                disabled={savingStep}
                onClick={() => setStepFormOpen(false)}
              >
                Cancel
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {commentModal && (
        <Dialog
          open
          onClose={closeCommentModal}
          fullWidth
          maxWidth="md"
          className="buyer-room__comment-modal"
          PaperProps={{ style: { "--dsr-accent": accent } }}
        >
          <div className="buyer-room__comment-modal-head">
            <div>
              <Typography
                owner="client"
                variant="overline"
                className="buyer-room__comment-modal-kicker"
              >
                {typeMeta[commentModal.content_type]?.label || "Content"}{" "}
                discussion
              </Typography>
              <Typography
                owner="client"
                variant="h6"
                className="buyer-room__comment-modal-title"
              >
                {commentModal.title}
              </Typography>
            </div>
            <IconButton
              onClick={closeCommentModal}
              size="small"
              aria-label="Close"
            >
              <CloseIcon />
            </IconButton>
          </div>
          <DialogContent dividers className="buyer-room__comment-modal-body">
            <RoomThread
              messages={modalMessages}
              onSend={(body, fids) =>
                sendMessage(body, fids, {
                  kind: "message",
                  content_type: commentModal.content_type,
                  content_id: commentModal.content_id,
                })
              }
              canPost={!room.preview}
              mine="buyer"
              uploadFile={uploadRoomFile}
              attachmentUrl={attachmentUrl}
              canUpload={!!identity.email && !room.preview}
              uploadHint={
                room.preview
                  ? "Attachments are disabled in preview."
                  : "Add your name and email above to attach files."
              }
              emptyText="No comments yet - start the discussion about this item."
              identitySlot={identityFormSlot}
            />
          </DialogContent>
        </Dialog>
      )}
    </main>
  );
}
